import {
  Box,
  Button,
  chakra,
  Flex,
  Input,
  SimpleGrid,
  Spinner,
  Switch,
  Text,
} from '@chakra-ui/react'
import AddressPrediction from '@components/common/form/address-prediction'
import { useStore } from '@contexts/store'
import { useCities, useCountries, useStates } from '@framework/get-locations'
import { Country } from '@framework/model-types/Country'
import { getGeoNamesbyLatLng } from '@framework/useGetGeoNamesbyLatLng'
import { useSWRGetUser } from '@framework/user/get-user'
import { addUserLocation } from '@framework/user/manage-user-address'
import { chakraSelectStyles } from '@helper/globalHelpers'
import useOnClickOutside from '@hooks/use-click-outside'
import { Select } from 'chakra-react-select'
import { isEmpty } from 'lodash'
import {
  ChangeEvent,
  Dispatch,
  useCallback,
  useMemo,
  useReducer,
  useRef,
} from 'react'
import usePlacesAutocompleteService from 'react-google-autocomplete/lib/usePlacesAutocompleteService'
import { AiOutlineLeft } from 'react-icons/ai'
import { parsePhoneNumber } from 'react-phone-number-input'
import { useModalAction, useModalState } from '../modal/modal.context'

const validGoogleStreetAddressTypes = [
  'street_address',
  'premise',
  'subpremise',
]

const AddEditAddressPopup = () => {
  const { isOpen, data } = useModalState()

  const { closeModal } = useModalAction()

  const backAction = () => {
    if (isOpen) {
      closeModal()
    }
  }

  return (
    <Box>
      <Text as="h3" fontWeight="600" fontSize="20px" mb="4">
        {data?.selectedAddress ? 'Edit Address' : 'Add New Address'}
      </Text>
      <AddEditAddress
        showBack={!!data?.selectedAddress}
        backAction={backAction}
        selectedAddress={data?.selectedAddress}
      />
    </Box>
  )
}

interface AddEditAddressProps {
  selectedAddress?: any
  setSelectedAddress?: Dispatch<any>
  backAction: (ignoreLastAddress?: boolean) => void
  showBack: boolean
}

type AddressState = ReturnType<typeof generateAddressState>

type ACTIONTYPE =
  | { type: 'SET_IS_DEFAULT'; payload: boolean }
  | { type: 'SET_SHOW_ADDRESS_PREDICTIONS'; payload: boolean }
  | { type: 'SET_ADDRESS_VALUE'; payload: string }
  | { type: 'SET_SHOW_LANDMARK_PREDICTIONS'; payload: boolean }
  | {
      type: 'REGENERATE_FORM_STATE'
      payload: AddressState
    }
  | { type: 'SET_LANDMARK_VALUE'; payload: string }
  | { type: 'SET_FULL_ADDRESS_VALUES'; payload: any }
  | { type: 'SET_COUNTRY_CODE'; payload: string }
  | { type: 'CLEAR_ADDRESS_FORM'; user: any }
  | { type: 'SET_FETCHING_PLACE_DETAILS'; payload: boolean }
  | { type: 'SET_SAVE_LOADING'; payload: boolean }
  | { type: 'SET_IS_MANUAL_FORM'; payload: boolean }
  | { type: 'SET_ADDITIONAL_DETAILS'; payload: string }
  | { type: 'SET_STATE_OPTION'; payload: string }
  | { type: 'SET_CITY_OPTION'; payload: string }
  | { type: 'SET_MANUAL_POSTAL_CODE'; payload: string }
  | { type: 'SET_ADDRESS_ERRORS'; payload: AddressState['errors'] }

function reducer(state: AddressState, action: ACTIONTYPE): AddressState {
  switch (action.type) {
    case 'SET_IS_DEFAULT':
      return { ...state, isDefaultAddress: action.payload }
    case 'SET_SHOW_ADDRESS_PREDICTIONS':
      return { ...state, showAddressPredictions: action.payload }
    case 'SET_ADDRESS_VALUE':
      return {
        ...state,
        addressSearchValue: action.payload,
        structuredAddressData:
          state.structuredAddressData && !state.isManualForm
            ? null
            : state.structuredAddressData,
      }
    case 'SET_SHOW_LANDMARK_PREDICTIONS':
      return { ...state, showLandmarkPredictions: action.payload }
    case 'REGENERATE_FORM_STATE':
      return { ...state, ...action.payload }

    case 'SET_LANDMARK_VALUE':
      return {
        ...state,
        landmarkSearchValue: action.payload,
        structuredAddressData: state.structuredAddressData
          ? null
          : state.structuredAddressData,
      }
    case 'SET_FULL_ADDRESS_VALUES':
      return {
        ...state,
        fullAddressValues: action.payload,
        structuredAddressData: generateStructuredData(action.payload),
        manualState: getManualState(action.payload),
        manualCity: getManualCity(action.payload),
        manualPostalCode: getManualPostalCode(action.payload),
      }
    case 'SET_FETCHING_PLACE_DETAILS':
      return { ...state, isFetchingPlaceDetails: action.payload }
    case 'SET_SAVE_LOADING':
      return { ...state, isSaveLoading: action.payload }

    case 'SET_COUNTRY_CODE':
      return {
        ...state,
        selectedCountryCode: action.payload,
      }
    case 'CLEAR_ADDRESS_FORM':
      return {
        ...state,
        ...generateAddressState({ selectedAddress: null, user: action.user }),
        isManualForm: state.isManualForm,
      }
    case 'SET_IS_MANUAL_FORM':
      return {
        ...state,
        addressType: action.payload ? 'close' : 'exact',
        isManualForm: action.payload,
        showAddressPredictions: action.payload
          ? false
          : state.showAddressPredictions,
      }
    case 'SET_ADDITIONAL_DETAILS':
      return { ...state, additionalDetails: action.payload }
    case 'SET_STATE_OPTION':
      return {
        ...state,
        manualState: { ...state.manualState, apiId: action.payload },
      }
    case 'SET_CITY_OPTION':
      return {
        ...state,
        manualCity: { ...state?.manualCity, apiId: action.payload },
      }
    case 'SET_MANUAL_POSTAL_CODE':
      return { ...state, manualPostalCode: action.payload }
    case 'SET_ADDRESS_ERRORS':
      return { ...state, errors: action.payload }

    default:
      throw new Error()
  }
}

const generateStructuredData = (fullAddressValues: any) => {
  if (!isEmpty(fullAddressValues)) {
    return {
      region: fullAddressValues?.geoLocation?.geoNames?.[3]?.name,
      state: fullAddressValues?.geoLocation?.geoNames?.[4]?.name
        ? fullAddressValues?.geoLocation?.geoNames?.[4]?.name
        : null,
      localty: fullAddressValues?.geoLocation?.geoNames?.[5]?.name
        ? fullAddressValues?.geoLocation?.geoNames?.[5]?.name
        : null,

      zip: fullAddressValues?.geoLocation?.zipCode
        ? fullAddressValues?.geoLocation?.zipCode
        : null,
    }
  }
  return null
}

const getManualState = (
  fullAddressValues: any,
): {
  apiId: string
  geoId: string
} => {
  if (!isEmpty(fullAddressValues)) {
    return {
      apiId: fullAddressValues?.stateData || '',
      geoId: fullAddressValues?.geoLocation?.geoNames?.[3]?.geonameId,
    }
  }
  return {
    geoId: '',
    apiId: '',
  }
}
const getManualCity = (
  fullAddressValues: any,
): {
  apiId: string
  geoId: string
} => {
  if (!isEmpty(fullAddressValues)) {
    return {
      apiId: fullAddressValues?.cityData || '',
      geoId: fullAddressValues?.geoLocation?.geoNames?.[4]?.geonameId || '',
    }
  }
  return {
    geoId: '',
    apiId: '',
  }
}

const getManualPostalCode = (fullAddressValues: any): string => {
  if (!isEmpty(fullAddressValues)) {
    return (
      fullAddressValues?.geoLocation?.zipCode ||
      fullAddressValues?.zip_code ||
      ''
    )
  }
  return ''
}

const generateAddressState = ({
  selectedAddress,
  user,
}: {
  selectedAddress: any
  user: any
}) => {
  const proximity = selectedAddress?.geoLocation?.addressProximity as
    | 'close'
    | 'exact'
  const isManualForm = proximity ? proximity !== 'exact' : !!selectedAddress
  const addressType = isManualForm ? 'close' : 'exact'

  const fullAddressValues = selectedAddress || null
  const structuredAddressData = generateStructuredData(fullAddressValues)

  return {
    isManualForm,
    addressType,
    isFetchingPlaceDetails: false,
    isSaveLoading: false,
    errors: {
      country: '',
      address: '',
      manualStateOption: '',
      manualCityOption: '',
    },
    isDefaultAddress: false,
    fullAddressValues,
    structuredAddressData,
    manualState: getManualState(selectedAddress),
    manualCity: getManualCity(selectedAddress),
    manualPostalCode: getManualPostalCode(selectedAddress),
    additionalDetails: selectedAddress?.line_2 || '',
    addressSearchValue:
      selectedAddress?.line_1 || selectedAddress?.address || '',
    selectedCountryCode:
      selectedAddress?.countryData?.code ||
      selectedAddress?.geoLocation?.geoNames?.[2]?.countryCode ||
      parsePhoneNumber(user?.person?.phone ?? '')?.country ||
      '',
    showAddressPredictions: false,
    showLandmarkPredictions: false,
    landmarkSearchValue: isManualForm
      ? !isEmpty(selectedAddress?.geoLocation)
        ? selectedAddress?.geoLocation?.fullAddress
        : selectedAddress?.line_2 || ''
      : '',
  }
}

export const AddEditAddress = ({
  selectedAddress,
  setSelectedAddress,
  backAction,
  showBack,
}: AddEditAddressProps) => {
  const {
    countries,
    isLoading: isCountriesLoading,
    isError: isCountriesError,
  } = useCountries()

  const { user, setUserState, analytics } = useStore()

  const {
    placesService,
    placePredictions,
    getPlacePredictions,
    isPlacePredictionsLoading,
  } = usePlacesAutocompleteService({
    apiKey: process.env.NEXT_PUBLIC_GOOGLE_PLACES_KEY,
  })

  const [addressState, dispatch] = useReducer(
    reducer,
    { selectedAddress, user },
    generateAddressState,
  )

  const {
    addressType,
    addressSearchValue,
    isDefaultAddress,
    landmarkSearchValue,
    selectedCountryCode,
    isManualForm,
    fullAddressValues,
    showAddressPredictions,
    showLandmarkPredictions,
    structuredAddressData,
    isFetchingPlaceDetails,
    additionalDetails,
    isSaveLoading,
    manualCity,
    manualState,
    manualPostalCode,
    errors,
  } = addressState

  const selectedCountryId =
    countries?.find((country) => country?.code === selectedCountryCode)?._id ||
    ''

  const {
    states,
    isLoading: isStatesLoading,
    isError: isStatesError,
  } = useStates(selectedCountryId)

  const manualStateId = useMemo(() => {
    return (
      manualState?.apiId ||
      states?.find((state) => state.geoId == manualState?.geoId)?._id ||
      ''
    )
  }, [manualState?.apiId, manualState?.geoId, states])

  const {
    cities,
    isLoading: isCitiesLoading,
    isError: isCitiesError,
  } = useCities(manualStateId)

  const manualCityId = useMemo(() => {
    return (
      manualCity?.apiId ||
      cities?.find((city) => city.geoId == manualCity?.geoId)?._id ||
      ''
    )
  }, [cities, manualCity?.apiId, manualCity?.geoId])

  const { mutate: mutateUser, key } = useSWRGetUser()

  const handleSetAsDefaultAddress = (e: ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'SET_IS_DEFAULT', payload: e.target.checked })
  }

  const countryOptions = useMemo(() => {
    return countries?.map((country: Country) => ({
      label: country?.name,
      value: country?.code,
    }))
  }, [countries])

  const stateOptions = useMemo(() => {
    return states?.map((state) => ({
      label: state?.name,
      value: state?._id,
    }))
  }, [states])

  const cityOptions = useMemo(() => {
    return cities?.map((city) => ({
      label: city?.name,
      value: city?._id,
    }))
  }, [cities])

  const countryCodeOption =
    countryOptions?.find(({ value }) => value === selectedCountryCode) ?? null

  const manualStateOption =
    stateOptions?.find(({ value }) => value === manualStateId) ?? null

  const manualCityOption =
    cityOptions?.find(({ value }) => value === manualCityId) ?? null

  const addressBoxRef = useRef<HTMLDivElement | null>(null)
  const landmarkBoxRef = useRef<HTMLDivElement | null>(null)

  useOnClickOutside(landmarkBoxRef, () => {
    dispatch({ type: 'SET_SHOW_LANDMARK_PREDICTIONS', payload: false })
  })

  useOnClickOutside(addressBoxRef, () => {
    dispatch({ type: 'SET_SHOW_ADDRESS_PREDICTIONS', payload: false })
  })

  const onAddressInputChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      if (errors?.address) {
        dispatch({
          type: 'SET_ADDRESS_ERRORS',
          payload: { ...errors, address: '' },
        })
      }
      if (!isManualForm) {
        getPlacePredictions({
          input: evt.target.value,
          componentRestrictions: { country: selectedCountryCode.toLowerCase() },
        })
      }
      dispatch({ type: 'SET_ADDRESS_VALUE', payload: evt.target.value })
      dispatch({ type: 'SET_SHOW_ADDRESS_PREDICTIONS', payload: true })
    },
    [getPlacePredictions, isManualForm, selectedCountryCode, errors],
  )

  const onAdditionalDetailsChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      dispatch({ type: 'SET_ADDITIONAL_DETAILS', payload: evt.target.value })
    },
    [],
  )
  const getPlaceDetails = (place: any) => {
    dispatch({ type: 'SET_FETCHING_PLACE_DETAILS', payload: true })
    placesService?.getDetails(
      {
        placeId: place?.place_id,
        fields: [
          'geometry',
          'formatted_address',
          'address_components',
          'adr_address',
          'name',
          'utc_offset_minutes',
          'types',
        ],
      },
      async (placeDetails: any) => {
        const placeName = validGoogleStreetAddressTypes?.some(
          (addressType) => placeDetails?.types?.includes(addressType),
        )
          ? placeDetails?.name
          : place?.structured_formatting?.main_text

        if (landmarkSearchValue) {
          dispatch({ type: 'SET_LANDMARK_VALUE', payload: placeName })
        } else {
          dispatch({ type: 'SET_ADDRESS_VALUE', payload: placeName })
        }
        // fetch GeoNames by Lat and Lng
        const geonameObj = await getGeoNamesbyLatLng(
          placeDetails?.geometry?.location?.lat(),
          placeDetails?.geometry?.location?.lng(),
        )

        let postcode = ''
        placeDetails?.address_components?.forEach((entry: any) => {
          if (entry.types?.[0] === 'postal_code') {
            postcode = entry.long_name
          }
        })
        dispatch({
          type: 'SET_FULL_ADDRESS_VALUES',
          payload: {
            ...fullAddressValues,
            line_1:
              isManualForm && addressSearchValue
                ? addressSearchValue
                : placeName,
            line_2: isManualForm ? placeName : additionalDetails,
            geoLocation: {
              ...fullAddressValues?.geoLocation,
              zipCode: postcode,
              geoNames: geonameObj?.geoNames,
              addressProximity: addressType,
              google: placeDetails,
              fullAddress: placeDetails?.formatted_address.replace(
                'null, ',
                '',
              ),
              coordinates: {
                lng: placeDetails?.geometry?.location?.lng(),
                lat: placeDetails?.geometry?.location?.lat(),
              },
            },
          },
        })
        dispatch({ type: 'SET_FETCHING_PLACE_DETAILS', payload: false })
      },
    )
  }

  const checkValidAddressForm = useCallback(() => {
    let valid = true
    const addressErrors = {} as typeof errors
    if (!selectedCountryCode) {
      addressErrors.country = 'Please select a country'
    }
    if (!isManualForm) {
      if (isEmpty(structuredAddressData)) {
        addressErrors.address =
          'Please select an address prediction or enter your address manually'
      }
    } else {
      if (!addressSearchValue) {
        addressErrors.address = 'Please type in your address'
      }
      if (!manualStateId) {
        addressErrors.manualStateOption = 'Please select a state'
      }
      if (!manualCityId) {
        addressErrors.manualCityOption = 'Please select a city'
      }
    }
    if (!isEmpty(addressErrors)) {
      dispatch({
        type: 'SET_ADDRESS_ERRORS',
        payload: { ...errors, ...addressErrors },
      })
      valid = false
    }
    return valid
  }, [
    addressSearchValue,
    errors,
    isManualForm,
    structuredAddressData,
    manualCityId,
    manualStateId,
    selectedCountryCode,
  ])

  const handleSaveAddress = useCallback(async () => {
    if (!checkValidAddressForm()) {
      dispatch({ type: 'SET_SAVE_LOADING', payload: false })
      return
    }
    dispatch({ type: 'SET_SAVE_LOADING', payload: true })
    const newAddress = {
      ...fullAddressValues,
    }
    if (isManualForm) {
      newAddress.address = `${addressSearchValue}, ${manualStateOption?.label}, ${countryCodeOption?.label}`
    } else if (
      validGoogleStreetAddressTypes?.some(
        (addressType) => newAddress?.google?.types?.includes(addressType),
      )
    ) {
      newAddress.address = newAddress?.geoLocation?.fullAddress
    } else {
      const adr = `${newAddress?.line_1}${newAddress?.geoLocation?.fullAddress?.substring(
        newAddress?.geoLocation?.fullAddress?.indexOf(','),
      )}`
      newAddress.address = adr
      newAddress.geoLocation.fullAddress = adr
    }

    newAddress.line_1 = addressSearchValue
    if (isManualForm) {
      if (isEmpty(newAddress?.geoLocation)) {
        newAddress.line_2 = landmarkSearchValue
      }
      newAddress.cityData = manualCityId
      newAddress.stateData = manualStateId
      newAddress.countryData = selectedCountryId
      newAddress.zip_code = newAddress?.geoLocation?.zipCode || manualPostalCode
    } else {
      newAddress['line_2'] = additionalDetails
    }
    newAddress.isDefault = isDefaultAddress
    let allAddresses = user?.person?.addresses ?? []
    if (user?.person?._id) {
      const { user: postedUser } = await addUserLocation(
        user?.person?._id,
        newAddress,
      )
      if (postedUser?.success) {
        allAddresses = postedUser?.data?.addresses
        setUserState({
          ...user,
          person: {
            ...user?.person,
            addresses: allAddresses,
          },
        })
        if (newAddress?.id) {
          if (setSelectedAddress)
            setSelectedAddress(
              allAddresses.find((address) => address?.id === newAddress?.id),
            )
        } else {
          if (setSelectedAddress)
            setSelectedAddress(
              (() => {
                if (isManualForm || isEmpty(newAddress.geoLocation)) {
                  return allAddresses?.findLast(
                    (address) =>
                      address?.cityData === newAddress?.cityData &&
                      address?.stateData === newAddress?.stateData &&
                      address?.countryData === newAddress?.countryData,
                  )
                }
                return allAddresses?.findLast(
                  (address) =>
                    address?.geoLocation?.coordinates?.lng ===
                      newAddress?.geoLocation?.coordinates?.lng &&
                    address?.geoLocation?.coordinates?.lat ===
                      newAddress?.geoLocation?.coordinates?.lat,
                )
              })(),
            )
        }

        mutateUser(key)
      }
    } else {
      if (selectedAddress?.id) {
        allAddresses = allAddresses?.map((entry: any) => {
          if (entry.id === selectedAddress?.id) {
            return newAddress
          }
          return entry
        })
      } else {
        allAddresses = [...allAddresses, newAddress]
      }
      if (setSelectedAddress) setSelectedAddress(newAddress)
    }

    const analyticsPayload = {
      email: user?.person?.email,
      fullName: user?.person?.fullname,
      phoneNumber: user?.person?.phone,
      addressEntered: newAddress,
      isManualAddress: isManualForm,
    }
    analytics.track('Address filled in', { ...analyticsPayload })

    setUserState({
      ...user,
      person: {
        ...user?.person,
        addresses: allAddresses,
      },
    })
    dispatch({ type: 'SET_SAVE_LOADING', payload: false })
    backAction(true)
  }, [
    fullAddressValues,
    user,
    setUserState,
    selectedAddress?.id,
    isDefaultAddress,
    backAction,
    key,
    mutateUser,
    setSelectedAddress,
    additionalDetails,
    addressSearchValue,
    isManualForm,
    manualPostalCode,
    selectedCountryId,
    checkValidAddressForm,
    landmarkSearchValue,
    manualCityId,
    manualStateId,
    countryCodeOption?.label,
    manualStateOption?.label,
    analytics,
  ])

  const onLandmarkInputChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      getPlacePredictions({
        input: evt.target.value,
        componentRestrictions: { country: selectedCountryCode.toLowerCase() },
      })
      dispatch({ type: 'SET_LANDMARK_VALUE', payload: evt.target.value })
      dispatch({ type: 'SET_SHOW_LANDMARK_PREDICTIONS', payload: true })
    },
    [getPlacePredictions, selectedCountryCode],
  )

  const setShowAddressInputPredictions = (payload: boolean) => {
    dispatch({ type: 'SET_SHOW_ADDRESS_PREDICTIONS', payload })
  }
  const setShowLandmarkInputPredictions = (payload: boolean) => {
    dispatch({ type: 'SET_SHOW_LANDMARK_PREDICTIONS', payload })
  }

  return (
    <>
      <Text as="h3" mb="3" fontWeight="700">
        {!isManualForm ? 'Auto-Fill Address' : 'Manual Address'}
      </Text>
      <Box mb="3.5">
        <Text mb="1.5 !important" fontWeight="500" fontSize="14px">
          Country
        </Text>
        <Select<(typeof countryOptions)[number]>
          isLoading={isCountriesLoading}
          name="countryCode"
          chakraStyles={chakraSelectStyles}
          isSearchable
          options={countryOptions}
          placeholder="Select your country"
          value={countryCodeOption}
          onChange={(option) => {
            if (option?.value) {
              dispatch({ type: 'CLEAR_ADDRESS_FORM', user })
              dispatch({
                type: 'SET_ADDRESS_ERRORS',
                payload: {
                  ...errors,
                  country: '',
                  address: '',
                  manualStateOption: '',
                },
              })
              dispatch({ type: 'SET_COUNTRY_CODE', payload: option.value })
              return
            }
          }}
        ></Select>
        {isCountriesError && !countries && (
          <Text color="danger" fontSize="13px">
            We could not fetch countries, please check your network connection
            or try again later
          </Text>
        )}
        {errors?.country ? (
          <Text color="danger" fontSize="13px">
            {errors?.country}
          </Text>
        ) : null}
      </Box>

      <Box id="addressBox" ref={addressBoxRef} mb="3.5">
        <Flex mb="1.5" align="center" justify="space-between">
          <Text as="span" fontWeight="500" fontSize="14px">
            Address
          </Text>
          <Text
            as="span"
            textDecoration="underline"
            cursor="pointer"
            color="black"
            fontSize="12px"
            className={
              !isManualForm &&
              addressSearchValue &&
              !showAddressPredictions &&
              isEmpty(structuredAddressData) &&
              !isFetchingPlaceDetails
                ? 'shake'
                : ''
            }
          >
            {isManualForm ? (
              <Text
                as="span"
                onClick={() =>
                  dispatch({ type: 'SET_IS_MANUAL_FORM', payload: false })
                }
              >
                Auto-Fill Address
              </Text>
            ) : (
              <Text
                as="span"
                onClick={() =>
                  dispatch({ type: 'SET_IS_MANUAL_FORM', payload: true })
                }
              >
                Enter Address Manually
              </Text>
            )}
          </Text>
        </Flex>
        <Box position="relative">
          <Flex justify="space-between" align="center" w="full">
            <Box position="relative" flexGrow="1">
              <Input
                textTransform="capitalize"
                isDisabled={!selectedCountryCode}
                type="text"
                fontWeight="700"
                fontSize="14px"
                placeholder="Search/Type your location"
                value={addressSearchValue}
                onChange={onAddressInputChange}
                onFocus={() => {
                  if (!isManualForm && addressSearchValue) {
                    dispatch({
                      type: 'SET_SHOW_ADDRESS_PREDICTIONS',
                      payload: true,
                    })
                  }
                }}
              />
              <Box
                onClick={() => {
                  if (!selectedCountryCode) {
                    dispatch({
                      type: 'SET_ADDRESS_ERRORS',
                      payload: {
                        ...errors,
                        address: 'Please select a country first',
                      },
                    })
                  } else {
                    dispatch({
                      type: 'SET_ADDRESS_ERRORS',
                      payload: {
                        ...errors,
                        address: '',
                      },
                    })
                  }
                }}
                display={selectedCountryCode ? 'none' : 'inline-block'}
                bg="transparent"
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
              ></Box>
            </Box>
            {isPlacePredictionsLoading && !isManualForm && (
              <Spinner
                maxW="10%"
                emptyColor="gray.200"
                ml="2.5"
                color="gray.400"
                thickness="3px"
                speed="0.70s"
                size="md"
              />
            )}
          </Flex>
          {errors?.address && (
            <Text color="danger" fontSize="13px">
              {errors?.address}
            </Text>
          )}

          {!isManualForm &&
            !isFetchingPlaceDetails &&
            addressSearchValue &&
            isEmpty(structuredAddressData) && (
              <Box
                className="address-predictions"
                position="absolute"
                top="44px"
                bg="white"
                left="0"
                right="0"
                zIndex="1"
                borderRadius="6px"
                boxShadow={
                  '0 0 10px 2px rgba(0, 0, 0, 0.05), 0 0 6px 1px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.1)'
                }
              >
                {placePredictions.length !== 0 && showAddressPredictions && (
                  <>
                    <Box
                      py=".4em"
                      px={{ base: '2', lg: '3' }}
                      borderRadius="6px"
                    >
                      <Text
                        fontSize={{ base: '13px', md: '15px' }}
                        fontWeight="600"
                        textAlign="center"
                      >
                        Select your address from the options below
                      </Text>
                    </Box>
                    <Flex direction="column" pt="1.5" pb="4" borderRadius="6px">
                      {placePredictions.map((place, i): any => {
                        return (
                          <AddressPrediction
                            key={i}
                            place={place}
                            getPlaceDetails={getPlaceDetails}
                            showPredictions={setShowAddressInputPredictions}
                          />
                        )
                      })}
                    </Flex>
                  </>
                )}

                <Box
                  py=".4em"
                  px="3"
                  borderRadius="6px"
                  _hover={{ bgColor: 'gray.100' }}
                  cursor="pointer"
                >
                  <Text
                    textAlign="center"
                    onClick={() => {
                      dispatch({ type: 'SET_IS_MANUAL_FORM', payload: true })
                    }}
                    fontSize="14px"
                    fontWeight="600"
                  >
                    Can't find your address? Click{' '}
                    <Text as="span" color="brand" textDecoration="underline">
                      here
                    </Text>{' '}
                    to enter address manually
                  </Text>
                </Box>
              </Box>
            )}
        </Box>
      </Box>

      {!isManualForm ? (
        <Box mb="3">
          <Text as="span" fontWeight="500" fontSize="14px">
            Additional Details {'('}Optional{')'}
          </Text>
          <Input
            type="text"
            fontSize="14px"
            fontWeight="700"
            placeholder="e.g Apartment No., Floor etc."
            value={additionalDetails}
            onChange={onAdditionalDetailsChange}
          />
        </Box>
      ) : null}

      {isManualForm && (
        <>
          <Box id="landmarkBox" ref={landmarkBoxRef} mb="3.5">
            <Text mb="1.5 !important" fontWeight="500" fontSize="14px">
              Nearest bus stop/landmark
            </Text>

            <Box position="relative">
              <Flex justify="space-between" align="center" w="full">
                <Box position="relative" flexGrow="1">
                  <Input
                    fontWeight="700"
                    fontSize="14px"
                    isDisabled={!selectedCountryCode}
                    type="text"
                    placeholder="Search/Type your location"
                    value={landmarkSearchValue}
                    onChange={onLandmarkInputChange}
                    onFocus={() => {
                      if (isManualForm && landmarkSearchValue) {
                        dispatch({
                          type: 'SET_SHOW_LANDMARK_PREDICTIONS',
                          payload: true,
                        })
                      }
                    }}
                  />
                </Box>
                {isPlacePredictionsLoading && (
                  <Spinner
                    maxW="10%"
                    emptyColor="gray.200"
                    ml="2.5"
                    color="gray.400"
                    thickness="3px"
                    speed="0.70s"
                    size="md"
                  />
                )}
              </Flex>
              <Box
                className="address-predictions"
                position="absolute"
                top="50px"
                zIndex="popover"
                bg="white"
                left="0"
                right="0"
                borderRadius="6px"
                boxShadow={
                  landmarkSearchValue && showLandmarkPredictions
                    ? '0 0 10px 2px rgba(0, 0, 0, 0.05), 0 0 6px 1px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.1)'
                    : ''
                }
              >
                {placePredictions.length >= 1 &&
                  showLandmarkPredictions &&
                  landmarkSearchValue && (
                    <>
                      <Box
                        py=".4em"
                        px={{ base: '2', lg: '3' }}
                        borderRadius="6px"
                      >
                        <Text
                          fontSize={{ base: '13px', md: '15px' }}
                          fontWeight="600"
                          textAlign="center"
                        >
                          Select your nearest bus stop/landmark from the options
                          below
                        </Text>
                      </Box>
                      <Flex direction="column" borderRadius="6px">
                        {placePredictions.map((place, i): any => {
                          return (
                            <AddressPrediction
                              key={i}
                              place={place}
                              getPlaceDetails={getPlaceDetails}
                              showPredictions={setShowLandmarkInputPredictions}
                            />
                          )
                        })}
                      </Flex>
                    </>
                  )}
              </Box>
            </Box>
          </Box>

          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={3} mb="3.5">
            <Box>
              <Text mb="1.5 !important" fontSize="14px" fontWeight="500">
                State
              </Text>

              <Select<(typeof stateOptions)[number]>
                isLoading={isStatesLoading}
                chakraStyles={chakraSelectStyles}
                onFocus={() => {
                  if (!selectedCountryId) {
                    dispatch({
                      type: 'SET_ADDRESS_ERRORS',
                      payload: {
                        ...errors,
                        manualStateOption: 'Please select a country first',
                      },
                    })
                  }
                }}
                name="state"
                placeholder="Select your state"
                isSearchable
                options={stateOptions}
                value={manualStateOption}
                onChange={(option) => {
                  if (option?.value) {
                    dispatch({
                      type: 'SET_STATE_OPTION',
                      payload: option?.value,
                    })
                    dispatch({
                      type: 'SET_ADDRESS_ERRORS',
                      payload: {
                        ...errors,
                        manualStateOption: '',
                        manualCityOption: '',
                      },
                    })
                  }
                }}
              />
              {isStatesError && isEmpty(states) && (
                <Text color="danger" fontSize="13px">
                  We could not fetch states, please select a country, check your
                  network connection or try again later
                </Text>
              )}
              {errors?.manualStateOption && (
                <Text color="danger" fontSize="13px">
                  {errors?.manualStateOption}
                </Text>
              )}
            </Box>
            <Box>
              <Text mb="1.5 !important" fontWeight="500" fontSize="14px">
                City
              </Text>

              <Select<(typeof cityOptions)[number]>
                isLoading={isCitiesLoading}
                name="city"
                isSearchable
                chakraStyles={chakraSelectStyles}
                placeholder="Select your city"
                options={cityOptions}
                value={manualCityOption}
                onFocus={() => {
                  if (!manualStateOption?.value) {
                    dispatch({
                      type: 'SET_ADDRESS_ERRORS',
                      payload: {
                        ...errors,
                        manualCityOption: 'Please select a state first',
                      },
                    })
                  }
                }}
                onChange={(option) => {
                  if (option?.value) {
                    dispatch({
                      type: 'SET_CITY_OPTION',
                      payload: option?.value,
                    })
                    dispatch({
                      type: 'SET_ADDRESS_ERRORS',
                      payload: { ...errors, manualCityOption: '' },
                    })
                  }
                }}
              />
              {isCitiesError && isEmpty(cities) && (
                <Text color="danger" fontSize="13px">
                  We could not fetch cities, please select a state, check your
                  network connection or try again later
                </Text>
              )}
              {errors?.manualCityOption && (
                <Text color="danger" fontSize="13px">
                  {errors?.manualCityOption}
                </Text>
              )}
            </Box>
          </SimpleGrid>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={3} mb="3.5">
            <Box>
              <Text mb="1.5 !important" fontWeight="500" fontSize="14px">
                Postal Code {'('}optional{')'}
              </Text>

              <Input
                textTransform="capitalize"
                type="text"
                fontWeight="700"
                fontSize="14px"
                placeholder="Enter your postal code"
                value={manualPostalCode}
                onChange={(event) =>
                  dispatch({
                    type: 'SET_MANUAL_POSTAL_CODE',
                    payload: event.target.value,
                  })
                }
              />
            </Box>
          </SimpleGrid>
        </>
      )}

      {!isEmpty(structuredAddressData) && !isManualForm && (
        <>
          <SimpleGrid
            columns={{ base: 1, xl: 2 }}
            rowGap="3.5"
            columnGap="15px"
          >
            {[
              {
                label: 'Region/Admin Zone',
                inputContent: structuredAddressData?.region,
              },
              {
                label: 'State/District',
                inputContent: structuredAddressData?.state,
              },
              {
                label: 'Locality/Area',
                inputContent: structuredAddressData?.localty,
              },
              {
                label: 'Zip/Postal code',
                inputContent: structuredAddressData?.zip,
              },
            ].map((addressData) => (
              <>
                {addressData?.inputContent && (
                  <Box key={addressData.label}>
                    <Text fontWeight="500" fontSize="14px">
                      {addressData.label}
                    </Text>
                    <Input
                      fontWeight="700"
                      fontSize="14px"
                      _disabled={{ bg: 'inputFieldOutline', color: 'black' }}
                      value={addressData?.inputContent}
                      readOnly
                      isDisabled
                    />
                  </Box>
                )}
              </>
            ))}
          </SimpleGrid>
        </>
      )}

      {user?.person?._id && (
        <Flex fontSize={'sm'} gap={2} color={'gray.500'} my="6" justify="end">
          <chakra.label
            alignSelf={'center'}
            htmlFor="default_shipping_address"
            flexShrink={0}
            color="black"
          >
            Set as default shipping address
          </chakra.label>
          <Switch
            isChecked={isDefaultAddress}
            onChange={handleSetAsDefaultAddress}
            alignSelf={'center'}
            colorScheme="primary"
            id="default_shipping_address"
          />
        </Flex>
      )}

      {isFetchingPlaceDetails && !isManualForm && (
        <Flex justify="center" my="4">
          <Spinner
            emptyColor="gray.200"
            ml="2.5"
            color="gray.400"
            thickness="3px"
            speed="0.70s"
            size="lg"
          />
        </Flex>
      )}

      <Flex justify="space-between" mt="3.5">
        {showBack && (
          <Button
            leftIcon={<AiOutlineLeft />}
            colorScheme="primary"
            variant="outline"
            onClick={() => {
              dispatch({ type: 'CLEAR_ADDRESS_FORM', user })
              backAction()
            }}
          >
            Back
          </Button>
        )}

        {isManualForm || (!isManualForm && !isEmpty(structuredAddressData)) ? (
          <Button
            colorScheme="primary"
            ms="auto"
            isDisabled={isSaveLoading}
            onClick={handleSaveAddress}
            className="pulse-button"
          >
            {selectedAddress ? 'Save changes' : 'Confirm Address'}
            {isSaveLoading && <Spinner size="sm" ml={2} />}
          </Button>
        ) : null}
      </Flex>
    </>
  )
}

export default AddEditAddressPopup
